import _ from 'lodash';
import queryString from 'query-string';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useUpdateEffect, useMount, useAsync } from 'react-use-v13';
import { AppBar, MuiTab, MuiTabs } from 'react-scale/components/design-system';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Spinner from 'design-system/CircularProgress';
import { LintRule } from '@scale/shared/types/linter';
import ResponseLinterLogs from './ResponseLinterLogs';
import ResponseLintersInfo from './ResponseLintersInfo';
import useQueryParams from 'react-scale/corp/hooks/useQueryParams';
import ResponseLintResults from './ResponseLintResults';
import { IProjectJson } from '@scale/shared/types/project';
import {
  ProjectLinterRulesResponse,
  SubtaskLintResult,
  ResponseLinterLog,
} from '@scale/shared/types/api/linting';

export enum ResponseLintersInfoTabs {
  Info = 'info',
  Logs = 'logs',
  Results = 'results',
}

const DEFAULT_TAB_INDEX = _.indexOf(
  _.values(ResponseLintersInfoTabs),
  ResponseLintersInfoTabs.Info,
);

const getTabIndex = (selectedTab: ResponseLintersInfoTabs) => {
  const index = _.indexOf(_.values(ResponseLintersInfoTabs), selectedTab);
  return index === -1 ? DEFAULT_TAB_INDEX : index;
};

const getTabName = (indexTab: number) => {
  return _.values(ResponseLintersInfoTabs)[indexTab];
};

const fetchSubtaskResult = async (subtaskId: string): Promise<SubtaskLintResult> => {
  const subtask = await apiFetch(null, `corp-api/linting/${subtaskId}/results`, {
    method: 'GET',
  });
  return subtask;
};

const ResponseLintersInfoPage: React.FC = () => {
  const query = useQueryParams();
  const history = useHistory();
  const [subtaskId, setSubtaskId] = useState<string | undefined>(() => {
    if (query?.subtask) {
      return Array.isArray(query.subtask) ? undefined : query.subtask;
    }
  });
  const [selectedProject, setSelectedProject] = useState<IProjectJson | undefined>();
  const [rules, setRules] = useState<LintRule<any>[]>();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState<number>(() => {
    // if query.subtask return results tab
    if (subtaskId) return getTabIndex(ResponseLintersInfoTabs.Results);

    // defaults to info tab
    if (!query?.tab || _.isArray(query.tab)) return DEFAULT_TAB_INDEX;

    const tab = getTabIndex(query.tab as ResponseLintersInfoTabs);
    return tab;
  });
  const [linterLogs, setLinterLogs] = useState<ResponseLinterLog[]>([]);
  const [subtaskLintResults, setSubtaskLintResults] = useState<SubtaskLintResult | undefined>();

  // get project if set in query-params
  useAsync(async () => {
    if (!selectedProject) {
      const paramsProjectId = _.isArray(query?.project) ? undefined : query.project;
      if (paramsProjectId) {
        try {
          const project = await apiFetch(null, `corp-api/lookup/${paramsProjectId}`, {
            method: 'GET',
          });
          setSelectedProject(project);
        } catch (error) {
          defaultApiFetchErrorHandler(error, { handleHttp: true });
          throw error;
        }
      }
    }
    if (subtaskId) {
      searchSubtaskLintResult(subtaskId, true);
    }
  }, []);

  useAsync(async () => {
    if (selectedProject) {
      try {
        setIsLoading(true);

        const [rulesResponse, logsResponse] = await Promise.all([
          apiFetch<ProjectLinterRulesResponse>(
            null,
            `corp-api/linting/${selectedProject._id}/rules`,
          ),
          apiFetch<ResponseLinterLog[]>(
            null,
            `corp-api/linting/responseLintersLogs/${selectedProject._id}`,
            {
              method: 'GET',
            },
          ),
        ]);

        setRules(rulesResponse.rules);
        setValidationErrors(rulesResponse.validationErrors);

        setLinterLogs(logsResponse);

        setIsLoading(false);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        throw error;
      }
    } else {
      setRules(undefined);
      setSelectedProject(undefined);
    }
  }, [selectedProject]);

  const shallowUpdateRoute = () => {
    const pathname = history.location.pathname;
    const search = queryString.stringify(
      _.pickBy({
        ...query,
        project: selectedProject?._id,
        subtask: subtaskId,
        tab: getTabName(tabIndex),
      }),
    );
    history.push({ pathname, search });
  };

  // update query-params onChange
  useMount(shallowUpdateRoute);
  useUpdateEffect(shallowUpdateRoute, [selectedProject, subtaskId, tabIndex]);

  const onSelectProject = (a?: any) => {
    let name: string | undefined;
    if (a?.label) {
      name = (a.label as string).split(`(${a.value})`)[0];
    }
    setSelectedProject(a ? ({ _id: a.value, name } as any) : undefined);
    setSubtaskId(undefined);
    setLinterLogs([]);
  };

  const searchSubtaskLintResult = async (subtaskId: string, isMount?: boolean) => {
    try {
      setIsLoading(true);
      const lintResult = await fetchSubtaskResult(subtaskId);
      setSubtaskLintResults(lintResult);
      setIsLoading(false);
      if (!isMount) setSubtaskId(subtaskId);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setIsLoading(false);
      throw error;
    }
  };

  if (isLoading) {
    return selectedProject ? (
      <div style={{ textAlign: 'center' }}>
        <h2>Loading {selectedProject?.name} info</h2>
        <div style={{ margin: 40 }}>
          <Spinner />
        </div>
      </div>
    ) : (
      <Spinner />
    );
  }

  const LinterLogs = () => {
    return (
      <ResponseLinterLogs
        linterLogs={linterLogs}
        onSelectProject={onSelectProject}
        selectedProject={selectedProject as IProjectJson}
      />
    );
  };

  const LinterRulesInfo = () => {
    return (
      <ResponseLintersInfo
        rules={rules}
        validationErrors={validationErrors}
        onSelectProject={onSelectProject}
        selectedProject={selectedProject as IProjectJson}
      />
    );
  };

  const LintResults = () => {
    return (
      <ResponseLintResults
        onSearch={searchSubtaskLintResult}
        isLoading={isLoading}
        subtaskLintResults={subtaskLintResults}
      />
    );
  };

  return (
    <MenuTabs
      LintResults={LintResults}
      LinterLogs={LinterLogs}
      LinterRulesInfo={LinterRulesInfo}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
    />
  );
};

const MenuTabs = (props: {
  LintResults: () => JSX.Element;
  LinterRulesInfo: () => JSX.Element;
  LinterLogs: () => JSX.Element;
  tabIndex: number;
  setTabIndex: (index: number) => void;
}) => {
  const { LintResults, LinterRulesInfo, LinterLogs, tabIndex, setTabIndex } = props;
  const TabPanel = (props: { children?: React.ReactNode; index: any; value: any }) => {
    const { children, value, index, ...other } = props;
    return (
      <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} {...other}>
        {value === index && children}
      </div>
    );
  };
  const tabProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };
  const handleChange = (_e: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <div style={{ marginTop: '-32px', marginRight: '-64px' }}>
      <AppBar position="static" style={{ marginLeft: '-32px' }}>
        <MuiTabs value={tabIndex} onChange={handleChange} aria-label="tabs-menu">
          <MuiTab label="Linter Rules Info" {...tabProps(0)} />
          <MuiTab label="Linter Config Logs/History" {...tabProps(1)} />
          <MuiTab label="Linter Results" {...tabProps(2)} />
        </MuiTabs>
      </AppBar>
      <TabPanel value={tabIndex} index={0}>
        <LinterRulesInfo />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <LinterLogs />
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <LintResults />
      </TabPanel>
    </div>
  );
};

export default ResponseLintersInfoPage;
